import { devWarn } from '@lemonade-hq/ts-helpers';

export const LMND_ORIGIN_NAMESPACE_HEADER = 'x-lmnd-origin-namespace';
export const LMND_ORIGIN_STAGE_HEADER = 'x-lmnd-origin-stage';

export interface BaseClientData {
  readonly serverBaseUrl: string;
  readonly accessTokenOverride?: string | null;
  readonly emailCode?: string | null;
}

export function extractNamespaceFromSubdomain(subdomain: string): string | null {
  const masterMatch = /.*-master$/.exec(subdomain);
  if (masterMatch) {
    return 'master';
  }

  const tDashMatch = /.*-(t-.+)$/.exec(subdomain);
  if (tDashMatch) {
    return tDashMatch[1];
  }

  const tMatch = /.*-(t[^-].*)$/.exec(subdomain);
  if (tMatch) {
    return tMatch[1];
  }

  return null;
}

export function extractOriginInfo(hostname: string): { readonly namespace: string; readonly stage: string } {
  try {
    if (hostname.endsWith('.lemonade.com')) {
      return { namespace: 'production', stage: 'production' };
    }

    if (hostname.includes('.lmndstaging.com')) {
      const subdomain = hostname.split('.')[0];
      const namespace = extractNamespaceFromSubdomain(subdomain);
      return { namespace: namespace ?? '', stage: 'staging' };
    }

    if (hostname.includes('.lmnddev.com')) {
      const subdomain = hostname.split('.')[0];
      const namespace = extractNamespaceFromSubdomain(subdomain);
      return { namespace: namespace ?? '', stage: 'development' };
    }

    return { namespace: '', stage: '' };
  } catch {
    return { namespace: '', stage: '' };
  }
}

export function createTrackingHeaders(): Record<string, string> {
  const { namespace, stage } = extractOriginInfo(window.location.hostname);
  return {
    'x-user-origin': window.location.href,
    'x-user-referer': document.referrer,
    [LMND_ORIGIN_NAMESPACE_HEADER]: namespace,
    [LMND_ORIGIN_STAGE_HEADER]: stage,
  };
}

export function appendQueryParams(
  path: URL,
  accessTokenOverride?: string | null,
  emailCode?: string | null,
  additionalParams?: Record<string, unknown>,
): string {
  if (accessTokenOverride != null) {
    path.searchParams.append('_at', accessTokenOverride);
  }

  if (emailCode != null) {
    path.searchParams.append('_ec', emailCode);
  }

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (typeof value === 'string') {
        path.searchParams.append(key, value);
      } else {
        devWarn(`❌ appendQueryParams: value is not a string: ${key} ${JSON.stringify(value)}`);
      }
    });
  }

  return path.toString();
}
